//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { Enums } from '../../models/common'
import { IReferenceType, IOpObject } from '../../models/util/interfaces'

import { ColumnModel } from '../../modules/api/api.models'

import { Dictionary, ToastHelper, LocalizationHelper, ConvertHelper, LogHelper, CommonHelper } from '../../helpers'

import { OpPackage, OpArticle, OpPackageStatus, OpDepositoryElement, OpOperator, OpDataTypeInGroup } from '../../models/core'

import { DataTypeGroupService, DATA_TYPE_GROUP_SERVICE_NAME } from '../data-type'
import { GRID_SERVICE_NAME, GridService } from '../grid'
import { ReferenceTypeService, REFERENCE_TYPE_SERVICE_NAME } from '../reference-type'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'

import { CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'


//#region Public Types

export const ORDER_SERVICE_NAME = 'OrderService'
export const ORDER_SERVICE_PROVIDER_NAME = 'OrderServiceProvider'

//#endregion


export class OrderService {


    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Order]' }
    public static get FILE_NAME_PREFIX(): string { return 'orders' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdPackage }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Order }


    //#endregion


    //#region Ctor

    constructor(
        private dataTypeGroupService: DataTypeGroupService,
        private gridService: GridService,
        private referenceTypeService: ReferenceTypeService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Articles

    public getArticles(): JQueryPromise<OpArticle[]> {
        return this.referenceTypeService.getDictionaryReferenceObjectList<OpArticle>(Enums.ReferenceType.IdArticle)
    }

    //#endregion


    //#region Columns

    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
        ], OrderService.SIMAX_MODULE_NAME)
    }

    public getConfirmOrderDepositoryColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
        ], Enums.SimaxModuleName.Location)
    }

    //#endregion


    //#region Content

    public getContentItemFromGridObject(data: any): OpDepositoryElement {
        return this.gridService.getSourceObjectFromGridObject<OpDepositoryElement>(data, Enums.ReferenceType.IdDepositoryElement)
    }

    //#endregion


    //#region Delete

    public deleteOrder(orderToDelete: OpPackage): JQueryPromise<void> {
        const toDelete: OpPackage = orderToDelete.clone()

        toDelete.opState = Enums.OpChangeState.Modified
        toDelete.dataList.setValue(Enums.PackageStatus.OrderRemoved, Enums.DataType.HELPER_ID_PACKAGE_STATUS)
        return this.userSessionService
            .getDataProvider().Order
            .deleteOrder(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, OrderService.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, OrderService.SIMAX_MODULE_NAME))
                LogHelper.warn(OrderService.LOG_PREFIX, `Unable to delete order for id ${orderToDelete.idPackage}`, error)
            })
    }

    //#endregion


    //#region Get

    public getLoggedOperator(): OpOperator {
        return this.userSessionService.getLoggedOperator()
    }

    public getOrder(idOrder: number) {
        return this.userSessionService
            .getDataProvider().Order
            .getOrder(idOrder)
            .then((order: OpPackage) => {
                if (order != null) {
                    return order
                }

                return $.Deferred().reject(null).promise()
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(OrderService.LOG_PREFIX, `Unable to load order for id ${idOrder}`, error)

                return null
            })
    }

    //#endregion


    //#region Order

    public getInitialOrderStatus(): JQueryPromise<OpPackageStatus> {
        return this.referenceTypeService
            .getDictionaryReferenceObjectDict(Enums.ReferenceType.IdPackageStatus)
            .then((packageStatusDict: Dictionary<OpPackageStatus>) => {
                if (packageStatusDict != null) {
                    return packageStatusDict.tryGetValue(Enums.PackageStatus.NewOrder)
                }

                return null
            })
    }

    //#endregion


    //#region Packages

    public getSourceObjectFromGridObject<TObject extends IReferenceType>(data: any): IReferenceType {
        return this.gridService.getSourceObjectFromGridObject(data, Enums.ReferenceType.IdPackage)
    }

    //#endregion


    //#region Parameters

    public getBasicParametrsDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.dataTypeGroupService.getDataTypeInGroupByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            OrderService.SIMAX_MODULE_NAME
        )
    }

    //#endregion


    //#region Save

    public saveOrder(orderToSave: OpPackage, isBulkSave: boolean = false): JQueryPromise<OpPackage> {
        return this.userSessionService
            .getDataProvider().Order
            .saveOrder(orderToSave)
            .then((savedObject: OpPackage) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, OrderService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.orderSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, OrderService.SIMAX_MODULE_NAME))
                LogHelper.warn(OrderService.LOG_PREFIX, `Unable to save order with id ${orderToSave.idDistributor}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion


    //#region Options

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null): JQueryPromise<CreateEditInitParams> {
        return this
            .getInitialOrderStatus()
            .then((initialOrderStatus: OpPackageStatus) => {
                const statusDictionary: Dictionary<OpPackageStatus[]> = new Dictionary<OpPackageStatus[]>(null)

                statusDictionary.addOrUpdate(Enums.DataType.HELPER_ID_PACKAGE_STATUS, [initialOrderStatus])

                const createEditOptions: CreateEditInitParams = {
                    createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
                    idReferenceType: OrderService.REFERENCE_TYPE,
                    moduleName: OrderService.SIMAX_MODULE_NAME,
                    sections: null,
                    useBusinessObjects: true,

                    referenceObject,
                    referenceObjectId,
                }

                if (referenceObjectId === 0 || referenceObject != null && referenceObject.idObject === 0) {
                    createEditOptions.customDataSources = statusDictionary
                }

                return createEditOptions
            })
    }

    //#endregion
}


export class OrderServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        DATA_TYPE_GROUP_SERVICE_NAME,
        GRID_SERVICE_NAME,
        REFERENCE_TYPE_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            dataTypeGroupService: DataTypeGroupService,
            gridService: GridService,
            referenceTypeService: ReferenceTypeService,
            userSessionService: UserSessionService
        ) {
            return new OrderService(dataTypeGroupService, gridService, referenceTypeService, userSessionService);
        }]

    //#endregion
} 